// ============================================================
// MobileTopBar.jsx
// The mobile shell's only persistent chrome: a hamburger, the
// current page's name, and the two counts that need attention.
//
// Carrying the badges here is the point, not decoration. On desktop
// the counts live in the side rail; on mobile that rail is
// off-canvas, so without them the operator would have to open a
// drawer to discover whether anything needed doing — and the reason
// this portal gets opened on a phone at all is to find out exactly
// that. A count that needs a tap to reach is a count nobody sees.
//
// Rendered only below the shell breakpoint (AppShell decides), so
// there is no desktop equivalent to keep in sync.
// ============================================================

import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Menu, Rocket, MessageSquare } from 'lucide-react';
import { titleForPath } from './navEntries';
import './MobileTopBar.css';

export default function MobileTopBar({ onOpenMenu, badges = {} }) {
  const location = useLocation();
  const title = titleForPath(location.pathname);

  const trialNew = badges.trialNew || 0;
  const contactNew = badges.contactNew || 0;

  return (
    <header className="mobile-top-bar">
      <button
        type="button"
        className="mobile-top-bar__menu"
        onClick={onOpenMenu}
        aria-label="Open navigation"
      >
        <Menu size={22} strokeWidth={1.9} />
      </button>

      <h2 className="mobile-top-bar__title">{title}</h2>

      {/* Each badge links straight to the surface it counts, so the
          number is not just information but the way in. Hidden at zero
          rather than shown as "0" — a permanent zero trains people to
          stop looking.

          The icon is the SAME one the nav uses for that destination,
          which does the disambiguating a word would: two unlabelled
          number pills side by side are a guess as to which is which.
          An icon also costs ~16px against ~45px for "enquiry", so both
          badges and a readable title fit down to 320px with no
          breakpoint dropping anything. Colour reinforces it; the
          accessible name carries the full wording either way. */}
      <div className="mobile-top-bar__badges">
        {trialNew > 0 && (
          <Link
            to="/trial-requests"
            className="mobile-top-bar__badge"
            aria-label={`${trialNew} new trial ${trialNew === 1 ? 'request' : 'requests'}`}
          >
            <Rocket size={14} strokeWidth={2} aria-hidden="true" />
            <span aria-hidden="true">{trialNew}</span>
          </Link>
        )}
        {contactNew > 0 && (
          <Link
            to="/contact-messages"
            className="mobile-top-bar__badge mobile-top-bar__badge--contact"
            aria-label={`${contactNew} unhandled ${contactNew === 1 ? 'enquiry' : 'enquiries'}`}
          >
            <MessageSquare size={14} strokeWidth={2} aria-hidden="true" />
            <span aria-hidden="true">{contactNew}</span>
          </Link>
        )}
      </div>
    </header>
  );
}
